/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B100910 マスタEXCEL出力
 */
import { Box } from "@mui/material";
import {
  B100910ExcelRequest,
  B100910GetExcel,
  catchFileError,
} from "clients/rest";
import {
  msgState,
  SConfirmDialog,
  SFileButton,
  SFrame,
  SLabel,
  SText,
  STextField,
} from "components";
import { getClientMessage } from "messages";
import { labels } from "messages/labels";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { download } from "services";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  gamenId: string;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  dataType: string;
  gamenId: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  dataType: "",
  gamenId: "",
};

const focusId = "B100910";

// 画面ID
// 名称マスタ
const gamenIdMeisho = "100020";
// 部門マスタ
const gamenIdBumon = "100040";
// 社員マスタ
const gamenIdShain = "100050";
// 銀行マスタ
const gamenIdGinko = "100090";
// 倉庫マスタ
const gamenIdSoko = "100100";
// 商品大分類マスタ
const gamenIdShodaibun = "100120";
// 商品中分類マスタ
const gamenIdShochubun = "100121";
// 商品小分類マスタ
const gamenIdShoshobun = "100122";
// ゾーン管理マスタ
const gamenIdZone = "100200";
// バッチマスタ
const gamenIdBatch = "100210";
// 配送ルートバッチマスタ
const gamenIdRoute = "100230";

// データ種別
// 名称マスタ
const dataTypeMeisho = "名称マスタ";
// 部門マスタ
const dataTypeBumon = "部門マスタ";
// 社員マスタ
const dataTypeShain = "社員マスタ";
// 銀行マスタ
const dataTypeGinko = "銀行マスタ";
// 倉庫マスタ
const dataTypeSoko = "倉庫マスタ";
// 商品大分類マスタ
const dataTypeShodaibun = "商品大分類マスタ";
// 商品中分類マスタ
const dataTypeShochubun = "商品中分類マスタ";
// 商品小分類マスタ
const dataTypeShoshobun = "商品小分類マスタ";
// ゾーン管理マスタ
const dataTypeZone = "ゾーン管理マスタ";
// バッチマスタ
const dataTypeBatch = "バッチマスタ";
// 配送ルートバッチマスタ
const dataTypeRoute = "配送ルートバッチマスタ";

export const B100910 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // 画面遷移
  const navigate = useNavigate();
  // エラー表示
  const setMsg = useSetRecoilState(msgState);

  // 初期表示イベント
  useEffect(() => {
    if (!props.gamenId) {
      return;
    }

    methods.setValue("gamenId", props.gamenId);

    // 名称マスタ
    if (props.gamenId === gamenIdMeisho) {
      methods.setValue("dataType", dataTypeMeisho);
    }
    // 部門マスタ
    else if (props.gamenId === gamenIdBumon) {
      methods.setValue("dataType", dataTypeBumon);
    }
    // 社員マスタ
    else if (props.gamenId === gamenIdShain) {
      methods.setValue("dataType", dataTypeShain);
    }
    // 銀行マスタ
    else if (props.gamenId === gamenIdGinko) {
      methods.setValue("dataType", dataTypeGinko);
    }
    // 倉庫マスタ
    else if (props.gamenId === gamenIdSoko) {
      methods.setValue("dataType", dataTypeSoko);
    }
    // 商品大分類マスタ
    else if (props.gamenId === gamenIdShodaibun) {
      methods.setValue("dataType", dataTypeShodaibun);
    }
    // 商品中分類マスタ
    else if (props.gamenId === gamenIdShochubun) {
      methods.setValue("dataType", dataTypeShochubun);
    }
    // 商品小分類マスタ
    else if (props.gamenId === gamenIdShoshobun) {
      methods.setValue("dataType", dataTypeShoshobun);
    }
    // ゾーン管理マスタ
    else if (props.gamenId === gamenIdZone) {
      methods.setValue("dataType", dataTypeZone);
    }
    // バッチマスタ
    else if (props.gamenId === gamenIdBatch) {
      methods.setValue("dataType", dataTypeBatch);
    }
    // 配送ルート管理マスタ
    else if (props.gamenId === gamenIdRoute) {
      methods.setValue("dataType", dataTypeRoute);
    }
    // gamenId に合致する dataType が存在しない場合
    else {
      methods.setValue("dataType", "");
    }

    // 初回フォーカス先
    focus(1);
  }, []);

  // EXCELボタン押下時
  const handleClickExcelButton = () => {
    const fields = methods.getValues();

    const param: B100910ExcelRequest = {
      gamenId: fields.gamenId!,
    };

    B100910GetExcel(param)
      .then((response) => {
        download(response);
        setMsg(getClientMessage("B100910_001"));
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="マスタEXCEL出力（全件共通）"
        gridTemplateColumns="7rem 15rem 10rem"
      >
        <SText gridColumn="span 3">　</SText>

        <SLabel>出力データ種別</SLabel>
        <STextField name="dataType" size={10} disabled />
        <Box />

        <SText gridColumn="span 3">　</SText>
        <SText gridColumn="span 3">　</SText>
        <SText gridColumn="span 3">　</SText>
        <SText gridColumn="span 3">　</SText>
        <SText gridColumn="span 3">　</SText>
        <SText gridColumn="span 3">　</SText>

        <Box gridColumn="span 2" />
        <SFileButton
          label={labels.buttons.excel}
          align="right"
          {...register(1)}
          dialog={
            <SConfirmDialog
              title={methods.watch("dataType") + "出力"}
              msg={labels.dialog.masterExcelConfirmMessage}
            />
          }
          dialogAction="confirm"
          handleSubmit={handleClickExcelButton}
        />
      </SFrame>
    </FormProvider>
  );
};
